import { Box, Chip, Grid, Tab, Tabs, Typography } from '@mui/material';

import StandardMap from '../../../../../components/Maps/StandardMap';
import { Constants } from '../../../../../constants/hiveNotices';
import {
  IconArrowCircleLeftMS,
  IconInfoMS,
} from '../../../../../constants/icons';
import { CommentsSection } from '../components/CompanyComments/CommentsSection';
import { LoadingSpinner } from '../components/LoadingSpinner';
import AcceptanceDocsTable from './components/AcceptanceDocsTable';
import AdministrativeDocumentsTable from './components/AdministrativeDocumentsTable';
import { ChangeRequestSummary } from './components/ChangeRequestSummary';
import { CompanyCard } from './components/CompanyData';
import DocumentsTable from './components/DocumentsTable';
import { EngineeringCriteriaSections } from './components/EngineeringCriteriaSections';
import { RequestDocument } from './components/RequestDocument';
import { TeamDocumentsSection } from './components/TeamDocumentsSection';
import {
  AccreditingForBox,
  CustomBackButton,
  GridSection,
  StatusAlert,
  StatusAlertIcon,
  StatusAlertText,
  TeamHintText,
} from './styles';
import useLicenseRequest from './useLicenseRequest';

export function LicenseRequest(): JSX.Element {
  const {
    loading,
    docLoading,
    companyData,
    noticeId,
    requestId,
    extraDocs,
    pendingChangeRequests,
    administrativeTerms,
    teamData,
    engineeringScope,
    accreditedCriteriaGroups,
    initiallyExpandedTeamMemberId,
    analysisTabs,
    currentTabKind,
    activeSections,
    handleChangeTab,
    isDiscredited,
    documentActionsDisabled,
    hasOpenChangeRequest,
    statusAlertMessage,
    hasCompanyCoords,
    searchMap,
    pinPlace,
    setPinPlace,
    navigateBack,
    handleLicenseRequest,
    handleApproveDocument,
    handleApproveWithReservation,
    handleReplaceDocument,
    handleRejectDocument,
    handleCancelExtraDocument,
    handleApproveTeamDocument,
    handleApproveTeamDocumentWithReservation,
    handleReplaceTeamDocument,
    handleRejectTeamDocument,
  } = useLicenseRequest();

  const isEngineeringTab = currentTabKind === 'engenharia';

  if (loading) {
    return <LoadingSpinner />;
  }
  return (
    <>
      <CustomBackButton onClick={navigateBack}>
        {IconArrowCircleLeftMS}
      </CustomBackButton>
      <Box mt={5}>
        {companyData && (
          <Grid container spacing={3}>
            <Grid item xs={7}>
              <CompanyCard
                noticeId={noticeId}
                data={companyData}
                callback={handleLicenseRequest}
              />
            </Grid>
            <Grid item xs={5}>
              <GridSection container>
                <Grid item xs={12}>
                  <StandardMap
                    searchMap={searchMap}
                    address={
                      hasCompanyCoords
                        ? undefined
                        : `${companyData.company.address} ${companyData.company.address_number}`
                    }
                    district={companyData.company.address_district}
                    city={companyData.company.city}
                    pinPlace={pinPlace}
                    setPinPlace={setPinPlace}
                    draggable={false}
                  />
                </Grid>
              </GridSection>
            </Grid>
          </Grid>
        )}
        {statusAlertMessage && (
          <StatusAlert role="status" aria-live="polite">
            <StatusAlertIcon aria-hidden="true">{IconInfoMS}</StatusAlertIcon>
            <StatusAlertText>{statusAlertMessage}</StatusAlertText>
          </StatusAlert>
        )}
        <ChangeRequestSummary requests={pendingChangeRequests} />
        {accreditedCriteriaGroups.length > 0 && (
          <AccreditingForBox>
            <Typography fontWeight={600}>
              {Constants.accreditingForLabel}
            </Typography>
            {accreditedCriteriaGroups.map((group) => (
              <Chip
                key={group.id}
                size="small"
                color="secondary"
                variant="outlined"
                label={group.name}
              />
            ))}
          </AccreditingForBox>
        )}
        <Typography my={5} fontSize={21} fontWeight={700}>
          {Constants.requiredDocumentationTitle}
        </Typography>
        {analysisTabs.length > 0 && (
          <Tabs
            value={currentTabKind}
            onChange={(_, value) => handleChangeTab(value)}
            textColor="secondary"
            indicatorColor="secondary"
            sx={{ mb: 3 }}
          >
            {analysisTabs.map((tab) => (
              <Tab
                key={tab.kind}
                value={tab.kind}
                label={tab.label}
                sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16 }}
              />
            ))}
          </Tabs>
        )}
        {currentTabKind === 'administrativo' && companyData ? (
          <>
            <AdministrativeDocumentsTable
              name={Constants.administrativeDocumentsSection}
              data={companyData.signed_contract}
              terms={administrativeTerms}
              docLoading={docLoading}
              handleApprove={handleApproveDocument}
              handleReject={handleRejectDocument}
              disabled={isDiscredited}
            />
            <AcceptanceDocsTable docList={companyData.acceptances || []} />
          </>
        ) : (
          <>
            <RequestDocument
              dataCallback={handleLicenseRequest}
              disabled={documentActionsDisabled}
            />
            {isEngineeringTab && engineeringScope === 'unavailable' && (
              <TeamHintText>{Constants.teamLoadError}</TeamHintText>
            )}
            {isEngineeringTab && engineeringScope === 'team' && (
              <TeamDocumentsSection
                noticeCompanyId={requestId}
                team={teamData}
                initiallyExpandedMemberId={initiallyExpandedTeamMemberId}
                pendingChangeRequests={pendingChangeRequests}
                actionsDisabled={isDiscredited}
                docLoading={docLoading}
                handleReplace={handleReplaceTeamDocument}
                handleApprove={handleApproveTeamDocument}
                handleApproveWithReservation={
                  handleApproveTeamDocumentWithReservation
                }
                handleReject={handleRejectTeamDocument}
              />
            )}
            {isEngineeringTab && engineeringScope === 'company' && (
              <EngineeringCriteriaSections
                noticeCompanyId={requestId}
                docLoading={docLoading}
                handleReplace={handleReplaceDocument}
                handleApprove={handleApproveDocument}
                handleApproveWithReservation={handleApproveWithReservation}
                handleReject={handleRejectDocument}
                disabled={documentActionsDisabled}
                hasOpenChangeRequest={hasOpenChangeRequest}
              />
            )}
            {!isEngineeringTab &&
              activeSections.map(({ name, docList }) => (
                <DocumentsTable
                  key={name}
                  name={name}
                  companyType={companyData?.company_type}
                  docList={docList}
                  docLoading={docLoading}
                  handleReplace={handleReplaceDocument}
                  handleApprove={handleApproveDocument}
                  handleApproveWithReservation={handleApproveWithReservation}
                  handleReject={handleRejectDocument}
                  disabled={documentActionsDisabled}
                  hasOpenChangeRequest={hasOpenChangeRequest}
                />
              ))}
            {!isEngineeringTab && extraDocs.length > 0 && (
              <DocumentsTable
                name={Constants.extraDocumentsSection}
                docList={extraDocs}
                docLoading={docLoading}
                handleReplace={handleReplaceDocument}
                handleApprove={handleApproveDocument}
                handleApproveWithReservation={handleApproveWithReservation}
                handleReject={handleRejectDocument}
                handleCancel={handleCancelExtraDocument}
                disabled={documentActionsDisabled}
                hasOpenChangeRequest={hasOpenChangeRequest}
              />
            )}
          </>
        )}
        <CommentsSection
          noticeId={noticeId}
          licensedCompanyId={companyData?.licensed_company_id}
        />
      </Box>
    </>
  );
}
